import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApiService, { NotFoundError } from './services/ApiService';
import UrlService from './services/UrlService';
import AuthorizationService from './services/AuthorizationService';
import DecryptionError from './services/DecryptionError';
import NotFoundScreen from './components/NotFoundScreen';
import { useQuicksort } from './hooks/useQuicksort';
import QuicksortModal from './components/QuicksortModal';
import VoteOptionsRenderer from './components/VoteOptionsRenderer';
import LinkText from './components/LinkText';
import LanguageSwitcher from './components/LanguageSwitcher';
import { usePageMetadata } from './hooks/usePageMetadata';

function RankedElectionUpdateVote() {
    const { t } = useTranslation(['voting', 'common', 'election']);
    
    const [rankedElection, setRankedElection] = useState({});
    
    // Page metadata
    const { title, description } = usePageMetadata(rankedElection);
    const [options, setOptions] = useState([]);
    const [voterName, setVoterName] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [isDecryptionError, setIsDecryptionError] = useState(false);
    const [isNotFoundError, setIsNotFoundError] = useState(false);
    const [notFoundType, setNotFoundType] = useState('vote');
    const { electionId, voteId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Quicksort functionality
    const {
        isActive: isQuicksortActive,
        currentComparison,
        progress: quicksortProgress,
        startSort,
        cancelSort,
        makeChoice
    } = useQuicksort();

    useEffect(() => {
        // Extract admin keys from URL
        UrlService.extractAndStoreAdminKeys(location, electionId, voteId);
        // Extract encryption keys from URL
        UrlService.extractAndStoreEncryptionKeys(location, electionId);

        setLoading(true);
        
        // Fetch both election details and vote details
        Promise.all([
            ApiService.getElection(electionId),
            ApiService.getVote(electionId, voteId)
        ])
        .then(([electionData, voteData]) => {
            setError(null);
            setIsDecryptionError(false);
            setRankedElection(electionData);
            setVoterName(voteData.voterName || '');

            // Sync URL with correct encryption params from localStorage
            UrlService.syncEncryptionKeysWithUrl(electionId);

            if (!voteData.options || !Array.isArray(voteData.options)) {
                setError(t('voting:update.invalidVoteData'));
                setLoading(false);
                return;
            }
            
            // First, reconstruct the voter's existing ordered options
            const sortedVoteOptions = voteData.options
                .sort((a, b) => a.rank - b.rank)
                .map(voteOption => {
                    // Use the correct field name for the option ID
                    const optionId = voteOption.rankedElectionOptionId || voteOption.optionId || voteOption.id;
                    return {
                        id: optionId,
                        name: voteOption.name,
                        isVetoed: voteOption.isVetoed || false,
                        rank: voteOption.rank,
                        isConnectedToNext: false, // Will be calculated below
                        rankGroup: null // Track which rank group this option belongs to
                    };
                })
                .filter(option => option.id && option.name); // Filter out invalid options

            // Reconstruct connection state based on ranks - items with the same rank should be connected
            const existingVoteOptions = sortedVoteOptions.map((option, index) => {
                // An option is connected to the next if:
                // 1. It's not the last option
                // 2. The next option has the same rank
                // 3. Neither this option nor the next is vetoed
                const nextOption = sortedVoteOptions[index + 1];
                const isConnectedToNext = nextOption &&
                    option.rank === nextOption.rank &&
                    !option.isVetoed &&
                    !nextOption.isVetoed;

                return {
                    ...option,
                    isConnectedToNext: isConnectedToNext || false
                };
            });
            
            // Find election options that are NOT in the voter's current vote
            const votedOptionIds = new Set(existingVoteOptions.map(option => option.id));
            const newElectionOptions = (electionData.options || [])
                .filter(electionOption => !votedOptionIds.has(electionOption.id))
                .map(electionOption => ({
                    id: electionOption.id,
                    name: electionOption.name,
                    isVetoed: false,
                    isConnectedToNext: false, // Track if this option is connected to the next one
                    rankGroup: null // Track which rank group this option belongs to
                }));
            
            // Combine existing vote options (in their current order) with new election options at the bottom
            const allOptions = [...existingVoteOptions, ...newElectionOptions];
            
            setOptions(allOptions);
            setLoading(false);
        })
        .catch(error => {
            console.error('Error fetching data:', error);
            if (error instanceof NotFoundError) {
                setIsNotFoundError(true);
                setNotFoundType(error.resourceType || 'vote');
            } else if (error instanceof DecryptionError) {
                setError(t('common:errors.decryptionFailed'));
                setIsDecryptionError(true);
            } else {
                setError(t('voting:update.failedToLoad'));
                setIsDecryptionError(false);
            }
            setLoading(false);
        });
    }, [electionId, voteId, location]); // eslint-disable-line react-hooks/exhaustive-deps

    // Check if user has permission to edit this vote
    useEffect(() => {
        if (rankedElection.id && !AuthorizationService.canEditVote(electionId, voteId, rankedElection)) {
            setError(t('voting:update.permissionDenied'));
        }
    }, [electionId, voteId, rankedElection]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStartSort = async () => {
        try {
            // Only sort ranked (non-vetoed) options
            const rankedOptions = options.filter(opt => !opt.isVetoed);
            const vetoedOptions = options.filter(opt => opt.isVetoed);
            const sortedRankedOptions = await startSort(rankedOptions);

            // Disconnect all sorted options - quicksort creates a completely new ranking
            const disconnectedSortedOptions = sortedRankedOptions.map(option => ({
                ...option,
                isConnectedToNext: false
            }));

            setOptions([...disconnectedSortedOptions, ...vetoedOptions]);
        } catch (error) {
            if (error.message !== 'Sort cancelled') {
                console.error('Error during quicksort:', error);
                setError(t('voting:update.failedToSort'));
            }
        }
    };

    // Calculate rank considering connections (sports-style ranking)
    function calculateOptionRank(option, index, rankedOptions) {
        // If this option is connected to the previous one, use the previous option's rank
        if (index > 0 && rankedOptions[index - 1].isConnectedToNext) {
            return calculateOptionRank(rankedOptions[index - 1], index - 1, rankedOptions);
        }
        // Sports-style ranking: count actual positions, not just rank groups
        // Each item takes up a position, so the rank is index + 1
        return index + 1;
    }

    async function handleUpdateVote() {
        setSubmitting(true);
        setError(null);

        // Separate ranked and vetoed options
        const rankedOptions = options.filter(opt => !opt.isVetoed);
        const vetoedOptions = options.filter(opt => opt.isVetoed);

        // Calculate the highest rank used by ranked options (considering connections)
        let highestRank = 0;
        rankedOptions.forEach((option, index) => {
            const rank = calculateOptionRank(option, index, rankedOptions);
            if (rank > highestRank) {
                highestRank = rank;
            }
        });

        // Vetoed options get the next rank after all ranked options
        const vetoRank = highestRank + 1;

        const voteOptions = [
            ...rankedOptions.map((option, index) => ({
                rankedElectionOptionId: option.id,
                rank: calculateOptionRank(option, index, rankedOptions),
                isVetoed: false
            })),
            ...vetoedOptions.map(option => ({
                rankedElectionOptionId: option.id,
                rank: vetoRank,
                isVetoed: true
            }))
        ];

        const voteData = {
            options: voteOptions,
            voterName: voterName.trim() || null
        };

        try {
            await ApiService.updateVote(electionId, voteId, voteData);
            navigate(UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes/${voteId}`, electionId));
        } catch (error) {
            console.error('Error updating vote:', error);
            setError(t('voting:update.failedToUpdate'));
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="vote-creation-container">
                <div className="container-fluid h-100">
                    <div className="row h-100 justify-content-center align-items-center">
                        <div className="col-12 text-center">
                            <div className="spinner-border text-light" role="status">
                                <span className="visually-hidden">{t('voting:states.loading')}</span>
                            </div>
                            <p className="text-light mt-3">{t('voting:update.loadingDetails')}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show not found error screen if election or vote was not found
    if (isNotFoundError) {
        return <NotFoundScreen type={notFoundType} />;
    }

    // Show decryption error screen if decryption failed
    if (isDecryptionError) {
        return (
            <div className="vote-creation-container">
                <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                    <LanguageSwitcher />
                </div>

                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="vote-creation-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">{t('common:errors.decryptionTitle')}</h4>
                                    <p>{t('common:errors.decryptionMessage')}</p>
                                    <hr />
                                    <p className="mb-0">{t('common:errors.decryptionAction')}</p>
                                </div>
                                <div className="mt-3">
                                    <Link to="/" className="btn btn-outline-secondary">
                                        {t('common:navigation.backToHome')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error && error.includes('permission')) {
        return (
            <div className="vote-creation-container">
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="vote-creation-card mt-5">
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                                <div className="vote-creation-nav">
                                    <Link to="/" className="btn btn-outline-secondary me-2">
                                        {t('voting:update.backToHome')}
                                    </Link>
                                    <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes/${voteId}`, electionId)} className="btn btn-outline-primary">
                                        {t('voting:update.backToVote')}
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <div className="vote-creation-container">
            <div className="language-switcher-container position-fixed top-0 end-0 m-3" style={{ zIndex: 1000 }}>
                <LanguageSwitcher />
            </div>
            
            <div className="container-fluid">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        {/* Navigation */}
                        <div className="vote-creation-nav mb-4 pt-4">
                            <Link to="/" className="btn btn-outline-light btn-sm me-2">
                                {t('voting:update.backToHome')}
                            </Link>
                            <Link to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes/${voteId}`, electionId)} className="btn btn-outline-light btn-sm">
                                {t('voting:update.backToVote')}
                            </Link>
                        </div>

                        {/* Election Info Card */}
                        <div className="vote-creation-card mb-4">
                            <div className="vote-creation-header">
                                <h1 className="vote-creation-title">{t('voting:update.title')}</h1>
                                <div className="election-info">
                                    <h2 className="election-title">{rankedElection.title}</h2>
                                    {rankedElection.description && (
                                        <p className="election-description"><LinkText text={rankedElection.description} /></p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Voting Interface */}
                        {options.length > 0 && (
                            <VoteOptionsRenderer
                                options={options}
                                onOptionsChange={setOptions}
                                onStartSort={handleStartSort}
                                isQuicksortActive={isQuicksortActive}
                                disabled={submitting}
                                title={t('voting:update.subtitle')}
                                subtitle={t('voting:cast.instructions')}
                                enableVeto={true}
                                showImagePreviews={true}
                            />
                        )}
                        
                        {options.length === 0 && !loading && !error && (
                            <div className="vote-creation-card mb-4">
                                <div className="text-center py-4 text-muted">
                                    {t('voting:update.noOptionsFound')}
                                </div>
                            </div>
                        )}

                        {/* Actions Card */}
                        <div className="vote-creation-card mb-4">
                            {error && !error.includes('permission') && (
                                <div className="alert alert-danger mb-3" role="alert">
                                    {error}
                                </div>
                            )}
                            
                            {/* Voter Name Input */}
                            <div className="mb-4">
                                <label htmlFor="voterName" className="form-label">
                                    <strong>{t('voting:update.voterNameLabel')}</strong>
                                </label>
                                <input
                                    type="text"
                                    id="voterName"
                                    className="form-control"
                                    placeholder={t('voting:update.voterNamePlaceholder')}
                                    value={voterName}
                                    onChange={(e) => setVoterName(e.target.value)}
                                    disabled={submitting}
                                    maxLength={255}
                                />
                                <div className="form-text text-muted">
                                    {t('voting:update.voterNameHelp')}
                                </div>
                            </div>
                            
                            <div className="vote-actions">
                                <button
                                    className="btn btn-success btn-lg vote-submit-btn"
                                    onClick={handleUpdateVote}
                                    disabled={submitting || options.filter(opt => !opt.isVetoed).length === 0}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            {t('voting:update.updatingVote')}
                                        </>
                                    ) : (
                                        t('voting:update.updateVote')
                                    )}
                                </button>

                                <Link
                                    to={UrlService.buildEncryptedElectionUrl(`/ranked-elections/${electionId}/votes/${voteId}`, electionId)}
                                    className="btn btn-outline-secondary btn-lg"
                                    style={{ pointerEvents: submitting ? 'none' : 'auto' }}
                                >
                                    {t('voting:update.cancel')}
                                </Link>
                            </div>
                            
                            <div className="mt-3 text-center">
                                <small className="text-muted">
                                    {t('voting:update.saveNote')}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Quicksort Modal */}
            <QuicksortModal
                show={isQuicksortActive}
                currentComparison={currentComparison}
                progress={quicksortProgress}
                onChoice={makeChoice}
                onCancel={cancelSort}
            />
            </div>
        </>
    );
}

export default RankedElectionUpdateVote;